import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { format, addDays } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import { createExceptionEvent } from '@/store/slices/scheduleSlice';
import { colors, font, space, radius } from '@/theme/chalkCourt';
import { ExceptionPrefill } from '@/utils/punchCoverage';
import { minutesBetweenTimes } from '@/utils/timeParser';
import TimeField from '@/components/TimeField';

interface Props {
  visible: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  /**
   * Request time for a specific, known window — e.g. covering a clock-in that
   * isn't on the schedule. Locks the date and opens with these times filled in.
   */
  prefill?: ExceptionPrefill | null;
  /** Context line shown under the title (replaces the generic subtitle). */
  subtitle?: string;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

/** Backend minimum length for requested time. */
const MIN_MINUTES = 15;

const fmtLength = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

/** One-tap reasons — the common answers, so a manager still gets a real note. */
const QUICK_REASONS = [
  'Covered a class',
  'Extra help needed',
  'Meeting / training',
  'Picked up a shift',
];

const initialFields = (prefill?: ExceptionPrefill | null) => ({
  // Always blank — staff name what they actually worked on.
  name: '',
  date: prefill?.date ?? format(addDays(new Date(), 1), 'yyyy-MM-dd'),
  start: prefill?.start ?? '09:00',
  end: prefill?.end ?? '12:00',
});

/**
 * Form for an employee to request an exception event — unscheduled time
 * that becomes part of their schedule once a manager approves it.
 */
const RequestTimeModal: React.FC<Props> = ({
  visible,
  onClose,
  onSubmitted,
  prefill,
  subtitle,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isCreatingEvent } = useSelector((state: RootState) => state.schedule);

  const [name, setName] = useState(() => initialFields(prefill).name);
  const [date, setDate] = useState(() => initialFields(prefill).date);
  const [start, setStart] = useState(() => initialFields(prefill).start);
  const [end, setEnd] = useState(() => initialFields(prefill).end);
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const reset = () => {
    const f = initialFields(prefill);
    setName(f.name);
    setDate(f.date);
    setStart(f.start);
    setEnd(f.end);
    setReason('');
    setFormError(null);
  };

  // Re-seed each time the sheet opens — the caller may open it for a different punch.
  useEffect(() => {
    if (visible) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, prefill?.date, prefill?.start, prefill?.end]);

  const close = () => {
    reset();
    onClose();
  };

  const validate = (): string | null => {
    if (!name.trim()) {
      return 'Add a short title for what you worked on.';
    }
    if (!DATE_RE.test(date)) {
      return 'Date must be in YYYY-MM-DD format.';
    }
    if (!TIME_RE.test(start) || !TIME_RE.test(end)) {
      return 'Enter a start and end time, like 9:30 AM.';
    }
    if (start >= end) {
      return 'End time must be after the start time.';
    }
    if ((minutesBetweenTimes(start, end) ?? 0) < MIN_MINUTES) {
      return `Requested time must be at least ${MIN_MINUTES} minutes.`;
    }
    if (!reason.trim()) {
      return 'Add a reason so a manager can review it.';
    }
    return null;
  };

  const submit = async () => {
    const v = validate();
    if (v) {
      setFormError(v);
      return;
    }
    setFormError(null);
    const result = await dispatch(
      createExceptionEvent({
        name: name.trim(),
        eventDate: date,
        start,
        end,
        exceptionReason: reason.trim(),
      })
    );
    if (createExceptionEvent.fulfilled.match(result)) {
      if (result.payload.success) {
        onSubmitted();
        close();
      } else {
        setFormError(result.payload.message || 'Request could not be submitted.');
      }
    } else {
      setFormError((result.payload as string) || 'Request could not be submitted.');
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={close}>
      <KeyboardAvoidingView
        style={styles.backdrop}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={styles.sheet}>
          <View style={styles.handle} />
          <View style={styles.headRow}>
            <Text style={styles.title}>{prefill ? 'ADD TO SCHEDULE' : 'REQUEST TIME'}</Text>
            <TouchableOpacity
              onPress={close}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
              <Icon name="close" size={24} color={colors.inkSoft} />
            </TouchableOpacity>
          </View>
          <Text style={styles.subtitle}>
            {subtitle ??
              'Add time you worked that isn\'t on your schedule, so it\'s paid.'}
          </Text>

          <ScrollView keyboardShouldPersistTaps="handled" style={styles.form}>
            <Text style={styles.label}>TITLE</Text>
            <TextInput
              style={styles.input}
              value={name}
              onChangeText={setName}
              accessibilityLabel="Title"
            />

            <Text style={styles.label}>DATE</Text>
            {prefill ? (
              // Locked to the punch's day — the request exists to cover that punch.
              <View style={[styles.input, styles.inputLocked]}>
                <Text style={styles.lockedText}>
                  {format(new Date(`${date}T00:00:00`), 'EEEE, MMM d')}
                </Text>
              </View>
            ) : (
              <TextInput
                style={styles.input}
                value={date}
                onChangeText={setDate}
                placeholder="YYYY-MM-DD"
                placeholderTextColor={colors.inkFaint}
                autoCapitalize="none"
              />
            )}

            <TimeField label="START" value={start} onChange={setStart} />
            <TimeField label="END" value={end} onChange={setEnd} />
            {(() => {
              const mins = minutesBetweenTimes(start, end);
              if (mins === null) return null;
              return (
                <Text style={[styles.length, mins < MIN_MINUTES && styles.lengthBad]}>
                  {mins <= 0
                    ? 'End time is before the start time'
                    : `${fmtLength(mins)} of time requested`}
                </Text>
              );
            })()}

            <Text style={styles.label}>REASON</Text>
            <View style={styles.chips}>
              {QUICK_REASONS.map((r) => {
                const on = reason === r;
                return (
                  <TouchableOpacity
                    key={r}
                    style={[styles.chip, on && styles.chipOn]}
                    activeOpacity={0.8}
                    onPress={() => setReason(r)}>
                    <Text style={[styles.chipText, on && styles.chipTextOn]}>{r}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <TextInput
              style={[styles.input, styles.inputMultiline]}
              value={reason}
              onChangeText={setReason}
              placeholder="Why are you requesting this time?"
              placeholderTextColor={colors.inkFaint}
              multiline
            />

            {formError && (
              <View style={styles.errorBox}>
                <Icon name="alert-circle" size={16} color={colors.accentDark} />
                <Text style={styles.errorText}>{formError}</Text>
              </View>
            )}
          </ScrollView>

          <TouchableOpacity
            style={[styles.submit, isCreatingEvent && styles.submitBusy]}
            activeOpacity={0.85}
            disabled={isCreatingEvent}
            onPress={submit}>
            <Text style={styles.submitText}>
              {isCreatingEvent ? 'SUBMITTING…' : prefill ? 'ADD TIME' : 'SUBMIT REQUEST'}
            </Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28,26,18,0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.bg,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    paddingHorizontal: space.lg,
    paddingBottom: space.xl,
    maxHeight: '90%',
  },
  handle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.lineStrong,
    marginTop: space.sm,
    marginBottom: space.md,
  },
  headRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontFamily: font.display,
    fontSize: 30,
    color: colors.ink,
    letterSpacing: -0.5,
  },
  subtitle: {
    fontFamily: font.body,
    fontSize: 13.5,
    lineHeight: 19,
    color: colors.inkSoft,
    marginTop: 2,
    marginBottom: space.md,
  },
  form: {
    flexGrow: 0,
  },
  label: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 1.6,
    color: colors.inkFaint,
    marginBottom: space.xs,
    marginTop: space.md,
  },
  input: {
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingHorizontal: space.md,
    paddingVertical: space.md,
    fontFamily: font.bodyMed,
    fontSize: 16,
    color: colors.ink,
  },
  inputLocked: {
    backgroundColor: colors.surfaceSunk,
  },
  lockedText: {
    fontFamily: font.bodyMed,
    fontSize: 16,
    color: colors.inkSoft,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.xs,
    marginBottom: space.sm,
  },
  chip: {
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.line,
    backgroundColor: colors.surface,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  chipOn: {
    borderColor: colors.accent,
    backgroundColor: colors.accentSoft,
  },
  chipText: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.inkSoft,
  },
  chipTextOn: {
    color: colors.accentDark,
  },
  inputMultiline: {
    minHeight: 84,
    textAlignVertical: 'top',
  },
  length: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.inkSoft,
    marginTop: space.sm,
  },
  lengthBad: {
    color: colors.accentDark,
  },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: radius.sm,
    padding: space.sm,
    marginTop: space.md,
  },
  errorText: {
    flex: 1,
    marginLeft: space.xs,
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.accentDark,
  },
  submit: {
    height: 56,
    borderRadius: radius.md,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: space.lg,
  },
  submitBusy: {
    opacity: 0.5,
  },
  submitText: {
    fontFamily: font.display,
    fontSize: 22,
    letterSpacing: 1,
    color: colors.onAccent,
  },
});

export default RequestTimeModal;
